<script>
  import { onMount } from "svelte";
  import { t } from 'svelte-i18n';
  import CardStats from "../components/Cards/CardStats.svelte";
  import CardBarChart from "../components/Cards/CardBarChart.svelte";
  import CardLineChart from "../components/Cards/CardLineChart.svelte";
  import CardTable from "../components/Cards/CardTable.svelte";
  import pb from "../lib/pocketbase";

  let isLoading = true;
  let error = null;

  // Dashboard stats for Nonosky Orders
  let stats = {
    totalOrders: 0,
    pendingOrders: 0,
    totalQuotations: 0,
    totalProducts: 0,
    totalCategories: 0,
    totalClients: 0,
    recentOrders: 0
  };

  // Charts data
  let orderStatusData = [
    { label: 'PENDING', value: 0 },
    { label: 'CONFIRMED', value: 0 },
    { label: 'SHIPPED', value: 0 },
    { label: 'DELIVERED', value: 0 },
    { label: 'CANCELLED', value: 0 }
  ];

  let monthlyOrdersData = [];

  // Table rows for recent orders
  let recentOrdersTableRows = [];
  let recentOrdersTableHeaders = ['Order #', 'Client', 'Status', 'Total', 'Date', 'Actions'];

  function formatDate(value) {
    if (!value) return '';
    try {
      return new Date(value).toLocaleDateString('es-GT');
    } catch (_) {
      return '';
    }
  }

  function formatCurrency(value) {
    if (!value) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  }

  async function loadStats() {
    try {
      // Totals for orders and quotations
      const [allOrders, orders, quotations, products, categories, clients] = await Promise.all([
        pb.collection('cotiza_cab').getList(1, 1, { $autoCancel: false }).catch(() => ({ totalItems: 0 })),
        pb.collection('cotiza_cab').getList(1, 1, { filter: 'sta = true', $autoCancel: false }).catch(() => ({ totalItems: 0 })),
        pb.collection('cotiza_cab').getList(1, 1, { filter: 'sta = false', $autoCancel: false }).catch(() => ({ totalItems: 0 })),
        pb.collection('producto').getList(1, 1, { $autoCancel: false }).catch(() => ({ totalItems: 0 })),
        pb.collection('categoria').getList(1, 1, { $autoCancel: false }).catch(() => ({ totalItems: 0 })),
        Promise.resolve({ totalItems: 0 }) // Client collection not available
      ]);

      stats.totalOrders = allOrders?.totalItems || 0;
      stats.pendingOrders = orders?.totalItems || 0;
      stats.totalQuotations = quotations?.totalItems || 0;
      stats.totalProducts = products?.totalItems || 0;
      stats.totalCategories = categories?.totalItems || 0;
      stats.totalClients = clients?.totalItems || 0;

      // Recent orders (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const recent = await pb.collection('cotiza_cab').getList(1, 200, { sort: '-created', $autoCancel: false }).catch(() => ({ items: [] }));
      const recentCount = (recent?.items || []).filter(o => {
        try { return new Date(o.created) >= thirtyDaysAgo; } catch { return false; }
      }).length;
      stats.recentOrders = recentCount;

      // Order type distribution (Orders vs Quotations)
      const [ordersCount, quotationsCount] = await Promise.all([
        pb.collection('cotiza_cab').getList(1, 1, { filter: 'sta = true', $autoCancel: false }).catch(() => ({ totalItems: 0 })),
        pb.collection('cotiza_cab').getList(1, 1, { filter: 'sta = false', $autoCancel: false }).catch(() => ({ totalItems: 0 }))
      ]);
      
      orderStatusData = [
        { label: 'Orders', value: ordersCount?.totalItems || 0 },
        { label: 'Quotations', value: quotationsCount?.totalItems || 0 }
      ];
    } catch (e) {
      console.error('Error loading stats:', e);
      error = 'No se pudieron cargar las estadísticas';
    }
  }

  async function loadMonthlyOrdersData() {
    try {
      // Compute last 6 months labels and counts for orders
      const now = new Date();
      const labels = [];
      const counts = [];
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const month = d.toLocaleString('es-GT', { month: 'short' });
        labels.push(month.charAt(0).toUpperCase() + month.slice(1, 3));
        const start = new Date(d.getFullYear(), d.getMonth(), 1);
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 1);
        const startIso = start.toISOString();
        const endIso = end.toISOString();
        
        const resp = await pb.collection('cotiza_cab').getList(1, 1, {
          filter: `created >= "${startIso}" && created < "${endIso}"`,
          $autoCancel: false
        }).catch(() => ({ totalItems: 0 }));
        counts.push(resp?.totalItems || 0);
      }
      monthlyOrdersData = labels.map((label, idx) => ({ label, value: counts[idx] }));
    } catch (e) {
      console.warn('No se pudo cargar datos mensuales:', e);
      monthlyOrdersData = [];
    }
  }

  async function loadRecentOrdersTable() {
    try {
      // Fetch recent orders sorted by creation date descending
      const resp = await pb.collection('cotiza_cab').getList(1, 10, {
        // expand: 'cliente', // Client collection not available
        sort: '-created',
        $autoCancel: false
      }).catch(() => ({ items: [] }));
      
      const items = resp?.items || [];
      recentOrdersTableRows = items.map(order => ({
        id: order.numero_cotiza || order.id,
        cliente: order.cliente_nombre || '-',
        estado: order.estado || '-',
        total: formatCurrency(order.total || 0),
        fecha: formatDate(order.created) || '—',
        acciones: 'View'
      }));
    } catch (e) {
      console.error('Error loading recent orders for table:', e);
      recentOrdersTableRows = [];
    }
  }

  onMount(async () => {
    try {
      await Promise.all([
        loadStats(),
        loadMonthlyOrdersData(),
        loadRecentOrdersTable()
      ]);
    } catch (e) {
      console.error('Error initializing dashboard:', e);
      error = 'Error al cargar el dashboard';
    } finally {
      isLoading = false;
    }
  });
</script>

<div class="flex flex-wrap">
  <div class="w-full xl:w-8/12 mb-12 xl:mb-0 px-4">
    <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
      <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
        <div class="flex flex-wrap items-center">
          <div class="relative w-full max-w-full flex-grow flex-1">
            <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">
              Orders Overview
            </h6>
            <h2 class="text-blueGray-700 text-xl font-semibold">
              Orders Created Last 6 Months
            </h2>
          </div>
        </div>
      </div>
      <div class="p-4 flex-auto">
        {#if isLoading}
          <div class="flex justify-center items-center h-64">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blueGray-600"></div>
          </div>
        {:else if error}
          <div class="text-center text-red-500 p-4">
            {error}
          </div>
        {:else}
          <CardLineChart 
            chartData={monthlyOrdersData}
            chartTitle="Orders Created by Month"
            chartSubtitle="Last 6 Months"
          />
        {/if}
      </div>
    </div>
  </div>
  <div class="w-full xl:w-4/12 px-4">
    <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
      <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
        <div class="flex flex-wrap items-center">
          <div class="relative w-full max-w-full flex-grow flex-1">
            <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">
              Order Status
            </h6>
            <h2 class="text-blueGray-700 text-xl font-semibold">
              Distribution by Status
            </h2>
          </div>
        </div>
      </div>
      <div class="p-4 flex-auto">
        {#if isLoading}
          <div class="flex justify-center items-center h-64">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blueGray-600"></div>
          </div>
        {:else if error}
          <div class="text-center text-red-500 p-4">
            {error}
          </div>
        {:else}
          <CardBarChart 
            chartData={orderStatusData}
            chartTitle="Order Status Distribution"
          />
        {/if}
      </div>
    </div>
  </div>
</div>

<div class="flex flex-wrap mt-4">
  <div class="w-full xl:w-4/12 px-4">
    <CardStats
      statSubtitle="Total Orders"
      statTitle={stats.totalOrders.toString()}
      statArrow="up"
      statPercent="3.48"
      statPercentColor="text-emerald-500"
      statDescripiron="Since last month"
      statIconName="fas fa-shopping-cart"
      statIconColor="bg-orange-500"
    />
  </div>
  <div class="w-full xl:w-4/12 px-4">
    <CardStats
      statSubtitle="Pending Orders"
      statTitle={stats.pendingOrders.toString()}
      statArrow="up"
      statPercent="12"
      statPercentColor="text-emerald-500"
      statDescripiron="Since last month"
      statIconName="fas fa-clock"
      statIconColor="bg-pink-500"
    />
  </div>
  <div class="w-full xl:w-4/12 px-4">
    <CardStats
      statSubtitle="Recent Orders"
      statTitle={stats.recentOrders.toString()}
      statArrow="down"
      statPercent="1.10"
      statPercentColor="text-orange-500"
      statDescripiron="Since yesterday"
      statIconName="fas fa-chart-line"
      statIconColor="bg-lightBlue-500"
    />
  </div>
</div>

<div class="flex flex-wrap mt-4">
  <div class="w-full xl:w-4/12 px-4">
    <CardStats
      statSubtitle="Total Quotations"
      statTitle={stats.totalQuotations.toString()}
      statArrow="up"
      statPercent="3.48"
      statPercentColor="text-emerald-500"
      statDescripiron="Since last month"
      statIconName="fas fa-file-invoice"
      statIconColor="bg-blue-500"
    />
  </div>
  <div class="w-full xl:w-4/12 px-4">
    <CardStats
      statSubtitle="Total Products"
      statTitle={stats.totalProducts.toString()}
      statArrow="up"
      statPercent="12"
      statPercentColor="text-emerald-500"
      statDescripiron="Since last month"
      statIconName="fas fa-box"
      statIconColor="bg-green-500"
    />
  </div>
  <div class="w-full xl:w-4/12 px-4">
    <CardStats
      statSubtitle="Total Categories"
      statTitle={stats.totalCategories.toString()}
      statArrow="down"
      statPercent="1.10"
      statPercentColor="text-orange-500"
      statDescripiron="Since yesterday"
      statIconName="fas fa-tags"
      statIconColor="bg-purple-500"
    />
  </div>
</div>

<div class="flex flex-wrap mt-4">
  <div class="w-full px-4">
    <div class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded">
      <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
        <div class="flex flex-wrap items-center">
          <div class="relative w-full max-w-full flex-grow flex-1">
            <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">
              Recent Orders
            </h6>
            <h2 class="text-blueGray-700 text-xl font-semibold">
              Latest Orders and Quotations
            </h2>
          </div>
        </div>
      </div>
      <div class="p-4 flex-auto">
        {#if isLoading}
          <div class="flex justify-center items-center h-32">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blueGray-600"></div>
          </div>
        {:else if error}
          <div class="text-center text-red-500 p-4">
            {error}
          </div>
        {:else}
          <CardTable 
            tableData={recentOrdersTableRows}
            tableHeaders={recentOrdersTableHeaders}
            tableTitle="Recent Orders and Quotations"
          />
        {/if}
      </div>
    </div>
  </div>
</div>